import React, { ChangeEvent, useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Body2, Heading3 } from '@/components/ui/typography'
import { Director } from 'api/Clients/Types/ClientDetailedDto'
import { Option, Select } from 'components/Select'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchCountriesAction } from 'store/identity/identity.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { isEmpty } from 'utils/is-empty'

import styles from './ClientDetailsCorporateForm.module.scss'

interface OuterProps {
  data: Director

  onChange(values: Partial<Director>): void
}

export const DirectorForm: React.FC<OuterProps> = ({ onChange, data }) => {
  const [form, setForm] = useState<Partial<Director>>({
    id: data?.id,
    name: data.name,
    street: data.street,
    city: data.city,
    state: data.state,
    zipCode: data.zipCode,
    country: data.country,
  })
  const [error, setErrors] = useState<string>()

  const identityApiClient = useApiClient(IdentityApiClient)

  const countries = useAppSelector((state) => state.identity.countries)

  const dispatch = useAppDispatch()

  useEffect(() => {
    if (isEmpty(countries)) {
      dispatch(fetchCountriesAction(identityApiClient))
    }
  }, [dispatch, countries]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [event.target.name]: event.target.value })
  }

  const handleZipCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setForm({ ...form, [name]: value })
    if (value.length > 32) {
      setErrors('Maximum character limit for ZIP/Postal Code is 32.')
    } else {
      setErrors(undefined)
    }
  }

  const handleCountryChange = (value: string) => {
    const country = (countries || []).find((x) => x.id === value)
    if (country) setForm({ ...form, country })
  }

  const handleSubmitForm = () => {
    validateForm()

    if (isValidForm()) {
      onChange(form)
    }
  }

  const validateForm = () => {
    if (!isValidForm()) {
      setErrors('Please fill required fields!')
    } else {
      setErrors(undefined)
    }
  }

  const isValidForm = () => {
    return Object.values(form).every((value) => {
      return !!value
    })
  }

  return (
    <div className={styles.container}>
      <div>
        <Heading3 className={styles.title}>Director</Heading3>
      </div>

      <div className={styles.field}>
        <Body2 as='label' bold>
          Name<span>*</span>
        </Body2>
        <div className={styles.input}>
          <input name='name' value={form.name} type='text' required onChange={handleChange} />
        </div>
      </div>

      <div className={styles.field}>
        <Body2 as='label' bold>
          Country of Residence<span>*</span>
        </Body2>
        <div className={styles.input}>
          <Select
            placeholder='Country of Residence'
            value={form.country?.id}
            allowClear
            showSearch
            fullWidth
            onChange={handleCountryChange}
          >
            {countries.map((country) => (
              <Option value={country.id} key={country.id}>
                {country.name}
              </Option>
            ))}
          </Select>
        </div>
      </div>

      <div className={styles.field}>
        <Body2 as='label' bold>
          Street Name, Street Number, Apt. Number<span>*</span>
        </Body2>
        <div className={styles.input}>
          <input name='street' value={form?.street} type='text' required onChange={handleChange} />
        </div>
      </div>

      <div className={styles.field}>
        <Body2 as='label' bold>
          City<span>*</span>
        </Body2>
        <div className={styles.input}>
          <input name='city' value={form?.city} type='text' required onChange={handleChange} />
        </div>
      </div>

      <div className={styles.field}>
        <Body2 as='label' bold>
          State/Region<span>*</span>
        </Body2>
        <div className={styles.input}>
          <input name='state' value={form?.state} type='text' required onChange={handleChange} />
        </div>
      </div>

      <div className={styles.field}>
        <Body2 as='label' bold>
          ZIP/Postal Code<span>*</span>
        </Body2>
        <div className={styles.input}>
          <input
            name='zipCode'
            value={form?.zipCode}
            type='text'
            required
            onChange={handleZipCodeChange}
          />
        </div>
      </div>

      <div className={styles.submit}>
        <Button type='button' onClick={handleSubmitForm} disabled={!!error}>
          Save
        </Button>
        {error && <div className={styles.error}>{error}</div>}
      </div>
    </div>
  )
}
